import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Divider,
  Box,
  Container,
  Drawer,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import { createTheme } from '@mui/material';
import { useEnglishMainContentLogic } from './EnglishMainContentLogics';
import './styles/App.css';
import { Link } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';

const EnglishMainContent: React.FC = () => {
  const {
    englishData,
    englishError,
    englishDarkMode,
    englishHandleThemeChange,
  } = useEnglishMainContentLogic();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<number | null>(null);
  const [currentChapter, setCurrentChapter] = useState<number | null>(null);
  const [expandedBook, setExpandedBook] = useState<number | null>(null);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  useEffect(() => {
    if (englishData?.body?.page?.book && englishData.body.page.book.length > 0) {
      setSelectedBook(0);
      setCurrentChapter(0);
    }
  }, [englishData]);

  const handleChapterSelect = (bookIndex: number, chapterIndex: number) => {
    setSelectedBook(bookIndex);
    setCurrentChapter(chapterIndex);
    setExpandedBook(bookIndex);
    closeDrawer();
  };

  const handleAccordionChange = (bookIndex: number) => {
    setExpandedBook((prevExpanded) =>
      prevExpanded === bookIndex ? null : bookIndex
    );
  };

  const theme = createTheme({
    palette: {
      mode: englishDarkMode ? 'dark' : 'light',
    },
    typography: {
      fontFamily: 'Siddhanta, Roboto, Helvetica, Arial, sans-serif',
    },
  });

  const renderParagraphs = (paragraphs: any) =>
    Array.isArray(paragraphs) ? (
      paragraphs.map((para: string, index: number) => (
        <Typography key={index} variant="body1" paragraph>
          {para}
        </Typography>
      ))
    ) : (
      <Typography variant="body1" paragraph>
        {paragraphs}
      </Typography>
    );

  const renderStoryContent = (story: any) => {
    if (Array.isArray(story)) {
      return story.map((item: any, index: number) => (
        <div key={index}>{item.p && renderParagraphs(item.p)}</div>
      ));
    } else if (story?.p) {
      return renderParagraphs(story.p);
    }
    return null;
  };

  const renderStorySection = (story: any, title: string) =>
    story ? (
      <>
        <Divider />
        <Typography variant="h5">{title}</Typography>
        {renderStoryContent(story)}
      </>
    ) : null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Katha Saritsagara
          </Typography>
          <Switch checked={englishDarkMode} onChange={englishHandleThemeChange} />
          <Link to="/">
            <button className="homeBtn">
              <HomeIcon style={{ fontSize: '31px' }} />
              <span style={{ fontSize: '10px' }}>Home</span>
            </button>
          </Link>
        </Toolbar>
      </AppBar>

      <Drawer open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: 250 }} role="presentation">
          <div className="drawerHeading">Explore Books</div>
          {englishData?.body?.page?.book?.length ? (
            englishData.body.page.book.map((book: any, bookIndex: number) => (
              <Accordion
                key={bookIndex}
                expanded={expandedBook === bookIndex}
                onChange={() => handleAccordionChange(bookIndex)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{book.title_book}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {book.chapter.map((chapter: any, chapterIndex: number) => (
                      <ListItem
                        key={chapterIndex}
                        button
                        selected={selectedBook === bookIndex && currentChapter === chapterIndex}
                        onClick={() => handleChapterSelect(bookIndex, chapterIndex)}
                      >
                        <ListItemText primary={chapter.title_chap} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography>No books available</Typography>
          )}
        </Box>
      </Drawer>

      <Container maxWidth="lg" className="ContentContainer">
        <Typography className="MainTitle" variant="h4">
          {englishData?.body?.page?.title
            ? englishData.body.page.title.split('\n').map((line: string, index: number) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))
            : 'Loading...'}
        </Typography>

        {englishError && <Typography color="error">{englishError}</Typography>}

        {selectedBook !== null && currentChapter !== null && (
          <Card variant="outlined">
            <CardContent>
              <Typography className="BookTitle" variant="h4">
                {englishData.body.page.book[selectedBook]?.title_book}
              </Typography>
              <div className="ChapterTitleHeading">
                {englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.title_chap}
              </div>
              {renderParagraphs(
                englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.p
              )}
              <Divider />
              <Box
                className="ScrollableContent"
                sx={{
                  marginTop: 2,
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: 2,
                }}
              >
               {englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.mainstory &&
                  renderStoryContent(englishData.body.page.book[selectedBook].chapter[currentChapter].mainstory)}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story1, 'Story 1')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story1?.story1a, 'Story 1a')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story1?.story1b, 'Story 1b')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2, 'Story 2')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2a, 'Story 2a')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2b, 'Story 2b')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2d, 'Story 2d')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2e, 'Story 2e')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story3, 'Story 3')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story3?.story3a, 'Story 3a')}
                {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story4, 'Story 4')}

              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default EnglishMainContent;
